import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useForm } from 'react-hook-form';
import {
  Button, Form, FormGroup, Label, Input,
} from 'reactstrap';
import { pick } from 'lodash';
import ImageCropper from '../modals/ImageCropper';
import {
  customSelectStyles,
  validationMessages, regexPattern, staticConstants, profileImageValidation, selectValidation, NUMERIC_VALUE, mapTagsFromAPIRequest,
} from '../../utils';
import InputError from '../shared/elments/InputError';
import { Country, State, City } from '../shared/region';
import { GET_GENRE } from '../../graphql-client/queries';

function EditArtistDetail(props) {
  const {
    toggle, data, submitCallBack, userId,
  } = props;
  const {
    register, handleSubmit, errors, setValue, clearError, setError, triggerValidation,
  } = useForm({ mode: 'onBlur', nativeValidation: false });
  const [form, setState] = useState({
    contactNumber: '',
    country: {},
    state: {},
    city: {},
    genre: '',
  });

  const { data: generData } = useQuery(GET_GENRE);
  const [genre, setGenere] = useState(null);
  const [artistImage, setArtistImage] = useState();
  const [contactEmail, setContactEmail] = useState();
  const [primaryContactName, setPrimaryContactName] = useState();
  const [image, setImage] = useState();
  const [modalObj, setModalObj] = useState({});
  const [isOpenCropModal, setIsOpenCropModal] = useState(false);
  const [artistFileInputRandString, setArtistFileInputRandString] = useState(String(Math.random()));

  useEffect(() => {
    generData && generData.getGenre
      && setGenere(mapTagsFromAPIRequest(generData.getGenre.data));
  }, [generData]);

  useEffect(() => {
    if (genre && props.data) {
      const genreData = [];
      genre && genre.forEach((ele) => {
        if (props.data.genre.includes(ele.value)) {
          genreData.push(ele);
        }
      });
      setValue([{ genre: genreData }]);
      setState({
        ...form, ...{ genre: genreData },
      });
    }
  }, [genre]);

  useEffect(() => {
    selectValidation(register, 'country');
    selectValidation(register, 'state');
    selectValidation(register, 'city');
    profileImageValidation(register, 'artistImageUpload');

    register(
      { name: 'genre', type: 'custom', message: validationMessages.REQUIRED },
      {
        validate: (value) => (Array.isArray(value) ? value.length > 0 : !!value),
      },
    );

    if (props && props.data) {
      const currentUserData = data;
      currentUserData.country && delete currentUserData.country.__typename;
      currentUserData.state && delete currentUserData.state.__typename;
      currentUserData.city && delete currentUserData.city.__typename;
      const preFilledData = {
        country: currentUserData.country,
        state: currentUserData.state,
        city: currentUserData.city,
      };
      setValue([
        { country: currentUserData.country },
        { state: currentUserData.state },
        { city: currentUserData.city },
        { primaryContactName: currentUserData.primaryContactName || '' },
        { name: currentUserData.name },
        { bio: currentUserData.bio },
        { contactEmail: currentUserData.email },
        { instagram: currentUserData.socialMedia.instagram || '' },
        { facebook: currentUserData.socialMedia.facebook || '' },
        { spotify: currentUserData.socialMedia.spotify || '' },
        { youtube: currentUserData.socialMedia.youtube || '' },
        { twitter: currentUserData.socialMedia.twitter || '' },
        { website: currentUserData.socialMedia.website || '' },
      ]);
      setPrimaryContactName(currentUserData.primaryContactName);
      setContactEmail(currentUserData.email);
      setArtistImage(currentUserData.artistImage);
      clearError(['country', 'state', 'city', 'genre']);
      setState({
        ...form, ...preFilledData,
      });
    }
  }, [props]);

  const handleCountryChange = (selectedOption) => {
    clearError('country');
    setValue('country', selectedOption);
    setError('state');
    setError('city');
    setValue('state', null);
    setValue('city', null);
    setState({
      ...form,
      country: selectedOption,
      state: {},
      city: {},
    });
  };

  const handleStateChange = (selectedOption) => {
    clearError('state');
    setValue('state', selectedOption);
    setError('city');
    setValue('city', null);
    setState({
      ...form,
      state: selectedOption,
      city: {},
    });
  };

  const handleCityChange = (selectedOption) => {
    clearError('city');
    setValue('city', selectedOption);
    setState({
      ...form,
      city: selectedOption,
    });
  };

  const handleGenreChange = (selectedOption) => {
    selectedOption && selectedOption.length && clearError('genre');
    !selectedOption && setError('genre');
    setValue('genre', selectedOption);
    setState({
      ...form,
      genre: selectedOption,
    });
  };

  const onSubmit = (formData) => {
    const profileData = pick(formData, ['name', 'bio', 'email', 'primaryContactName']);
    profileData.userId = userId;
    profileData.city = formData.city;
    profileData.state = formData.state;
    profileData.country = formData.country;
    profileData.genre = formData.genre.map((el) => el.value);
    profileData.socialMedia = pick(formData, ['facebook', 'spotify', 'instagram', 'youtube', 'twitter', 'website']);
    if(artistImage) {
      profileData.artistImage = artistImage;
    }
    submitCallBack(profileData);
  };

  const handleImageSubmit = async (e, fieldName) => {
    const profileImageUpload = e[0];
    setValue(fieldName, e[0]);
    const success = await triggerValidation(fieldName);
    if (success) {
      setImage(profileImageUpload);
      setIsOpenCropModal(true);
      setModalObj({
        callback: (img) => setArtistImage(img),
      });
    }
  };

  const hideFileSelectModal = () => {
    setIsOpenCropModal(false);
    setArtistFileInputRandString(String(Math.random()));
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="modal-edit-artist-bio">
        <span>
          {isOpenCropModal && (
          <ImageCropper
            isOpen={isOpenCropModal}
            image={image}
            savedCroppedImage={modalObj.callback}
            onClose={hideFileSelectModal}
          />
          )}
          <Dropzone onDrop={(acceptedFiles) => handleImageSubmit(acceptedFiles, 'artistImageUpload')}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <section className={isDragActive ? 'DragActive' : ''}>
              <div {...getRootProps()} className="signup-form-image-section signup-form-edit-image-section">
                <input name="artistImageUpload" {...getInputProps()} key={artistFileInputRandString} multiple={false} accept="image/*" />
                <Label className={artistImage ? 'border-0' : ''}>
                  {artistImage ? (
                    <img
                      src={artistImage}
                      className="img-cover"
                      alt="profile"
                    />
                  )
                    : <></>}
                  <span>Upload artist picture <p className="required">*</p></span>
                  <em className="icon icon-camera" />
                </Label>
              </div>
            </section>
          )}
        </Dropzone>
        <InputError error={errors.artistImageUpload} className="image-error-text" max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
        </span>
        <div className="modal-edit-artist-bio-content">
          <h4>Primary contact</h4>
          <p>{primaryContactName}</p>
          <p>{contactEmail}</p>
        </div>
        <div className="modal-edit-artist-bio-input">
          <FormGroup className={errors.name ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Name"
            >
              Name
            </Label>
            <Input
              type="text"
              name="name"
              value={form.name}
              placeholder="Enter name"
              innerRef={register({ required: true, maxLength: NUMERIC_VALUE.TWO_HUNDRED, minLength: NUMERIC_VALUE.THREE })}
            />
            <InputError error={errors.name} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
          </FormGroup>
          <FormGroup className={errors.bio ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Bio"
            >
              A short bio
            </Label>
            <Input
              type="textarea"
              className="large"
              name="bio"
              value={form.bio}
              placeholder="Enter a short bio"
              maxLength={NUMERIC_VALUE.FIVE_HUNDRED}
              innerRef={register({ required: true })}
            />
            <InputError error={errors.bio} max={NUMERIC_VALUE.FIVE_HUNDRED} />
          </FormGroup>
        </div>
      </div>
      <div className="modal-edit-artist-name">
        <FormGroup className={errors.genre ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Genre"
          >
            Genre
          </Label>
          <Select
            className="reactSelect multiSelect"
            styles={customSelectStyles}
            name="genre"
            placeholder="Enter genre"
            options={genre}
            value={form.genre}
            onChange={handleGenreChange}
            isMulti
          />
          <InputError error={errors.genre} />
        </FormGroup>
        <FormGroup className={errors.country ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Country"
          >
            Country
          </Label>
          <Country
            onChange={handleCountryChange}
            value={form.country}
          />
          <InputError error={errors.country} maxLength={NUMERIC_VALUE.TWO_HUNDRED} minLength={NUMERIC_VALUE.THREE} />
        </FormGroup>
        <div className="country-input-group">
          <FormGroup className={errors.state ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="State"
            >
              State
            </Label>
            <State
              onChange={handleStateChange}
              country={form.country}
              value={form.state}
            />
            <InputError error={errors.state} />
          </FormGroup>
          <FormGroup className={errors.city ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="City"
            >
              City
            </Label>
            <City
              onChange={handleCityChange}
              state={form.state}
              value={form.city}
            />
            <InputError error={errors.city} />

          </FormGroup>
        </div>
      </div>
      <div className="modal-edit-artist-social">
        <h3>Social media links</h3>
        <FormGroup className={errors.facebook ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Facebook"
          >
            Facebook
          </Label>
          <Input
            type="text"
            name="facebook"
            data-label="facebook"
            innerRef={register({
              type: 'text',
              pattern: { value: regexPattern.FACEBOOK, message: validationMessages.FACEBOOK },
            })}
          />
          <InputError error={errors.facebook} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.TEN} />

        </FormGroup>
        <FormGroup className={errors.instagram ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Instagram"
          >
            Instagram
            {' '}
            <em className="required-icon">*</em>
          </Label>
          <Input
            type="text"
            name="instagram"
            data-label="instagram"
            innerRef={register({
              required: true,
              type: 'text',
              pattern: { value: regexPattern.INSTAGRAM, message: validationMessages.INSTAGRAM },
            })}
          />
          <InputError error={errors.instagram} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
        </FormGroup>
        <FormGroup className={errors.spotify ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Spotify"
          >
            Spotify/Soundcloud (Optional)
          </Label>
          <Input
            type="text"
            name="spotify"
            data-label="spotify"
            innerRef={register({
              type: 'text',
              pattern: { value: regexPattern.SPOTIFY, message: validationMessages.SPOTIFY },
            })}
          />
          <InputError error={errors.spotify} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />

        </FormGroup>
        <FormGroup className={errors.youtube ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="YouTube"
          >
            YouTube (Optional)
          </Label>
          <Input
            type="text"
            name="youtube"
            data-label="youtube"
            innerRef={register({
              type: 'text',
              pattern: { value: regexPattern.YOUTUBE, message: validationMessages.YOUTUBE },
            })}
          />
          <InputError error={errors.youtube} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
        </FormGroup>
        <FormGroup className={errors.twitter ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Twitter"
          >
            Twitter (Optional)
          </Label>
          <Input
            type="text"
            name="twitter"
            data-label="twitter"
            innerRef={register({
              type: 'text',
              pattern: { value: regexPattern.TWITTER, message: validationMessages.TWITTER },
            })}
          />
          <InputError error={errors.twitter} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
        </FormGroup>
        <FormGroup className={errors.website ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
          <Label
            for="Website"
          >
            Website (Optional)
          </Label>
          <Input
            type="text"
            name="website"
            data-label="website"
            innerRef={register({
              type: 'text',
              pattern: { value: regexPattern.WEBSITE, message: validationMessages.WEB },
            })}
          />
          <InputError error={errors.website} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
        </FormGroup>
      </div>
      <div className="modal-edit-artist-btn-group">
        <span onClick={toggle}>Cancel</span>
        <Button
          color="accept"
        >
          Update
        </Button>
      </div>
    </Form>
  );
}

EditArtistDetail.propTypes = {
  toggle: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
  submitCallBack: PropTypes.func.isRequired,
};

export default EditArtistDetail;
